import React, { useState, useEffect } from 'react';
import { DocumentList } from './components/document-list';
import { DocumentDetails } from './components/document-details';
import { Navbar } from './components/nav-bar';
import { EquipmentSelector } from './components/equipment-selector';
import { LoginPage } from './LoginPage';
import { useAuth } from './AuthContext';
import { ApiService } from './services/api-service';
import { EquipmentType } from './enums';


export interface Document {
  documentId: string;
  originalFilename: string;
  status: 'COMPLETE' | 'PROCESSING' | 'FAILED' | 'UNKNOWN';
  completedAt?: string;
  progress?: number;
  totalSections?: number;
  completedSections?: number;
  sections?: Section[];
}

export interface Section {
  sectionId: string;
  status: string;
  pageNumber?: number;
  errorMessage?: string;
}

const App: React.FC = () => {
  const { isAuthenticated, isAuthenticating, signOut } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const [equipmentType, setEquipmentType] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [documentDetails, setDocumentDetails] = useState<Document | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all documents
  const fetchDocuments = async (model?: string) => {
    try {
      setLoading(true);
      const documents = await ApiService.fetchDocuments(model || selectedModel);
      setDocuments(documents);
      setError(null);
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication required') {
        signOut(); // Force sign out if token is invalid
      }
      setError('Failed to fetch documents. Please try again later.');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch details for a specific document
  const fetchDocumentDetails = async (documentId: string) => {
    try {
      setLoading(true);
      const document = await ApiService.fetchDocumentDetails(documentId);
      setDocumentDetails(document);
      setError(null);
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication required') {
        // Handle auth errors specifically
        signOut(); // Force sign out if token is invalid
      }
      setError(`Failed to fetch details for document ${documentId}.`);
      console.error('Error fetching document details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentModels = async (equipmentType: EquipmentType) => {
    try {
      setLoading(true);
      const fetchedModels = await ApiService.fetchEquipmentModels(equipmentType);
      setModels(fetchedModels);
      setLoading(false);
      return fetchedModels;
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication required') {
        signOut(); // Force sign out if token is invalid
      }
      setError(`Failed to fetch equipment models for ${equipmentType}.`);
      console.error('Error fetching equipment models:', err);
      setLoading(false);
      return [];
    }
  };

  const handleTypeChange = async (selectedType: string) => {
    setDocuments([])
    if (selectedType) {
      setEquipmentType(selectedType);
      await fetchEquipmentModels(selectedType as EquipmentType);
    }
  };

  const handleModelChange = (selectedModel: string | null) => {
    setSelectedDocument(null);
    setSelectedModel(selectedModel || '');
    // When model changes, fetch documents with the new model
    if (isAuthenticated) {
      fetchDocuments(selectedModel || '');
    }
  };

  //Load document details when selected document changes
  useEffect(() => {
    console.log('use effect 2')
    if (selectedDocument && isAuthenticated) {
      fetchDocumentDetails(selectedDocument);
    } else {
      setDocumentDetails(null);
    }
  }, [selectedDocument, isAuthenticated]);

  // Handle document selection
  const handleSelectDocument = (documentId: string) => {
    console.log('handle select document')
    setSelectedDocument(documentId);
  };

  // Handle back button
  const handleBack = () => {
    console.log('hanlde bakc')
    setSelectedDocument(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    console.log('hanlde refresh')
    if (isAuthenticated) {
      if (selectedDocument) {
        fetchDocumentDetails(selectedDocument);
      } else {
        fetchDocuments();
      }
    }
  };

  // Show loading state while checking authentication
  if (isAuthenticating) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // If authenticated, show application
  return (
    <div className="app">
      <Navbar 
        onRefresh={handleRefresh} 
        onSignOut={signOut} 
        showSignOut={true} 
      />
      <div className="container mt-4">
      <EquipmentSelector
        onTypeChange={handleTypeChange}
        onModelChange={handleModelChange}
        models={models}
        loading={loading}
      />
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {loading && !documents.length ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {selectedDocument && documentDetails ? (
              <DocumentDetails 
                document={documentDetails} 
                onBack={handleBack} 
              />
            ) : (
              <DocumentList 
                documents={documents} 
                onSelectDocument={handleSelectDocument} 
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default App;